import { Box, Stack, Typography, alpha, useTheme } from "@mui/material";
import { fPercent } from "src/utils/format-number";

interface PaidShoppingShareTooltipProps {
    lastShoppingVisibilityRate?: number;
    topShoppingVisibilityRate?: number;
    competShoppingVisibilityRate?: number;
    onMouseLeave?: () => void;
}

export default function PaidShoppingShareTooltip({
    lastShoppingVisibilityRate = 0,
    topShoppingVisibilityRate = 0,
    competShoppingVisibilityRate = 0,
    onMouseLeave = () => {},
}: PaidShoppingShareTooltipProps) {
    const theme = useTheme();
    const formatPercent = (value: any) => value > 1 ? value.toFixed(0) + "%" : value + "%";
    
    // Calculate percentages
    // last_shopping_visibility_rate is for 1st position
    // top_shopping_visibility_rate is for 2-3 position
    const firstPercent = Math.min(Math.max(lastShoppingVisibilityRate, 0), 100);
    const topPercent = Math.min(Math.max(topShoppingVisibilityRate, 0), 100);
    const competPercent = Math.min(Math.max(competShoppingVisibilityRate, 0), 100);
    
    // Calculate total for bar visualization
    const total = firstPercent + topPercent + competPercent;
    const firstBarWidth = total > 0 ? (firstPercent / total) * 100 : 0;
    const topBarWidth = total > 0 ? (topPercent / total) * 100 : 0;
    const competBarWidth = total > 0 ? (competPercent / total) * 100 : 0;

    return (
        <Box
            sx={{
                background: `linear-gradient(to right, ${theme.palette.grey[800]}, ${theme.palette.grey[800]})`,
                border: `1px solid ${alpha(theme.palette.grey[600], 0.3)}`,
                borderRadius: '12px',
                overflow: 'hidden',
                minWidth: 250,
            }}
            onMouseLeave={onMouseLeave}
        >
            {/* Header */}
            <Box
                sx={{
                    backgroundColor: alpha(theme.palette.common.white, 0.05),
                    px: 2,
                    py: 2,
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: 1,
                        color: theme.palette.common.white,
                    }}
                >
                    Paid Shopping Share
                </Typography>
            </Box>

            {/* Bar Chart */}
            <Box
                sx={{
                    px: 2,
                    py: 2,
                }}
            >
                <Stack direction="row" spacing={0.5} sx={{ width: '100%', height: 12 }}>
                    {/* 1st position segment (orange) */}
                    {firstBarWidth > 0 && (
                        <Box
                            sx={{
                                flex: firstBarWidth,
                                height: 12,
                                backgroundColor: '#FF9A0B',
                                borderRadius: '2px',
                                minWidth: firstBarWidth > 0 ? '2px' : 0,
                            }}
                        />
                    )}
                    {/* 2-3 position segment (lighter orange) */}
                    {topBarWidth > 0 && (
                        <Box
                            sx={{
                                flex: topBarWidth,
                                height: 12,
                                backgroundColor: '#FFD89F',
                                borderRadius: '2px',
                                minWidth: topBarWidth > 0 ? '2px' : 0,
                            }}
                        />
                    )}
                    {/* Competitors segment (grey) */}
                    {competBarWidth > 0 && (
                        <Box
                            sx={{
                                flex: competBarWidth,
                                height: 12,
                                backgroundColor: '#637381',
                                borderRadius: '2px',
                                minWidth: competBarWidth > 0 ? '2px' : 0,
                            }}
                        />
                    )}
                </Stack>
            </Box>

            {/* Legend */}
            <Box
                sx={{
                    backgroundColor: alpha(theme.palette.common.white, 0.03),
                    px: 2,
                    py: 2,
                }}
            >
                <Stack spacing={1.5}>
                    {/* 1st position */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                            <Box
                                sx={{
                                    width: 6,
                                    height: 20,
                                    backgroundColor: '#FF9A0B',
                                    borderRadius: '16px',
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: 1,
                                    color: alpha(theme.palette.common.white, 0.8),
                                }}
                            >
                                1st position
                            </Typography>
                        </Stack>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: 1,
                                color: theme.palette.common.white,
                                textAlign: 'right',
                            }}
                        >
                            {formatPercent(firstPercent)}
                        </Typography>
                    </Stack>

                    {/* 2-3 position */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                            <Box
                                sx={{
                                    width: 6,
                                    height: 20,
                                    backgroundColor: '#FFD89F',
                                    borderRadius: '16px',
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: 1,
                                    color: alpha(theme.palette.common.white, 0.8),
                                }}
                            >
                                2-3 position
                            </Typography>
                        </Stack>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: 1,
                                color: theme.palette.common.white,
                                textAlign: 'right',
                            }}
                        >
                            {formatPercent(topPercent)}
                        </Typography>
                    </Stack>

                    {/* Competitors */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                            <Box
                                sx={{
                                    width: 6,
                                    height: 20,
                                    backgroundColor: '#637381',
                                    borderRadius: '16px',
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: 1,
                                    color: alpha(theme.palette.common.white, 0.8),
                                }}
                            >
                                Competitors
                            </Typography>
                        </Stack>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: 1,
                                color: theme.palette.common.white,
                                textAlign: 'right',
                            }}
                        >
                            {formatPercent(competPercent)}
                        </Typography>
                    </Stack>
                </Stack>
            </Box>
        </Box>
    );
}

